import React, { useEffect, useState, useRef } from 'react';
import { api } from '../services/api';

function SearchableSelect({ id, options, value, onChange, disabled, placeholder }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const selected = options.find((o) => String(o.value) === String(value));

  const filtered = query.trim()
    ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;

  useEffect(() => {
    const handleClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSelect = (opt) => {
    onChange(opt.value);
    setQuery('');
    setOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setQuery('');
    setOpen(false);
  };

  return (
    <div className={`ss-wrap${disabled ? ' ss-disabled' : ''}`} ref={wrapRef}>
      <div className="ss-control" onClick={() => !disabled && setOpen((o) => !o)}>
        {open ? (
          <input
            autoFocus
            className="ss-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar programa..."
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span className={`ss-value${!selected ? ' ss-placeholder' : ''}`}>
            {selected ? selected.label : placeholder}
          </span>
        )}
        <div className="ss-icons">
          {selected && !open && (
            <span className="ss-clear" onClick={handleClear}>✕</span>
          )}
          <span className="ss-arrow">{open ? '▲' : '▼'}</span>
        </div>
      </div>
      {open && (
        <div className="ss-dropdown">
          {filtered.length === 0 ? (
            <div className="ss-no-results">Sin resultados</div>
          ) : (
            filtered.map((opt) => (
              <div
                key={opt.value}
                className={`ss-option${String(opt.value) === String(value) ? ' ss-selected' : ''}`}
                onMouseDown={() => handleSelect(opt)}
              >
                {opt.label}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default function Step1Process({ state, onUpdate, onNext, addToast }) {
  const [processes, setProcesses] = useState([]);
  const [details, setDetails] = useState([]);
  const [processInfo, setProcessInfo] = useState(null);
  const processesRef = useRef([]);
  const detailsRef = useRef([]);

  useEffect(() => {
    api.getProcesses()
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        processesRef.current = list;
        setProcesses(list);
        if (list.length === 1) handleProcessChange(String(list[0].id), list);
      })
      .catch(() => addToast('error', 'No se pudo cargar los procesos de admisión.'));
  }, []);

  const handleProcessChange = (processId, list) => {
    const procs = list || processesRef.current;
    const proc = procs.find((p) => String(p.id) === String(processId));
    const processLabel = proc ? `${proc.name}${proc.period ? ` — ${proc.period}` : ''}` : null;

    onUpdate({ selectedProcessId: processId || null, selectedDetailId: null, selectedProgramId: null, processLabel, programLabel: null });
    setProcessInfo(null);

    if (!proc) { setDetails([]); detailsRef.current = []; return; }

    const dets = proc.Admission_process_details || proc.details || proc.programs || [];
    detailsRef.current = dets;
    setDetails(dets);

    if (dets.length === 1) handleDetailChange(String(dets[0].id), dets);
  };

  const handleDetailChange = async (detailId, dets) => {
    const list = dets || detailsRef.current;
    const det = list.find((d) => String(d.id) === String(detailId));

    if (!det) {
      onUpdate({ selectedDetailId: null, selectedProgramId: null, selectedCostId: null });
      setProcessInfo(null);
      return;
    }

    const programId = det.id_program || det.programId || det.id;
    const denomination = det.Program?.description || det.Program?.denomination || det.name || det.nombre || `Programa #${det.id}`;
    onUpdate({ selectedDetailId: Number(detailId), selectedProgramId: programId, programLabel: denomination, selectedCostId: null });
    setProcessInfo({ denomination, costs: null, inscriptionCosts: [], loading: true });

    if (det.id_admision_plan) {
      try {
        const costs = await api.getCosts(det.id_admision_plan);
        const allCosts = Array.isArray(costs) ? costs : [];
        const inscriptionCosts = allCosts.filter(c => c.Concept && /inscripci[oó]n/i.test(c.Concept.denomination));
        setProcessInfo({ denomination, costs: allCosts, inscriptionCosts, loading: false });
        if (inscriptionCosts.length === 1) {
          onUpdate({ selectedCostId: inscriptionCosts[0].id });
        }
      } catch {
        setProcessInfo({ denomination, costs: null, inscriptionCosts: [], loading: false });
      }
    } else {
      setProcessInfo({ denomination, costs: null, inscriptionCosts: [], loading: false });
    }
  };

  const hasInscriptionCosts = processInfo && !processInfo.loading && processInfo.inscriptionCosts && processInfo.inscriptionCosts.length > 0;
  const canContinue = state.selectedProcessId && state.selectedDetailId && (!hasInscriptionCosts || state.selectedCostId);
  const detailLabel = (d) => d.Program?.description || d.Program?.denomination || d.name || d.nombre || `Programa #${d.id}`;

  const detailOptions = details.map((d) => ({ value: d.id, label: detailLabel(d) }));

  return (
    <div className="card">
      <div className="card-header">
        <div className="card-icon">🎓</div>
        <div>
          <div className="card-title">Selección de Proceso</div>
          <div className="card-sub">Elija el proceso de admisión y el programa al que desea postular</div>
        </div>
      </div>

      <div className="form-grid">
        <div className="form-group">
          <label className="form-label" htmlFor="sel-process">Proceso de Admisión</label>
          <select
            id="sel-process"
            className="form-select"
            value={state.selectedProcessId || ''}
            onChange={(e) => handleProcessChange(e.target.value)}
          >
            <option value="">Seleccione un proceso...</option>
            {processes.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}{p.period ? ` — ${p.period}` : ''}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">Programa</label>
          <SearchableSelect
            id="sel-detail"
            options={detailOptions}
            value={state.selectedDetailId || ''}
            onChange={(val) => val ? handleDetailChange(val) : onUpdate({ selectedDetailId: null, selectedProgramId: null })}
            disabled={!details.length}
            placeholder={details.length ? 'Seleccione o busque un programa...' : 'Primero seleccione un proceso'}
          />
          {state.selectedProcessId && !details.length && (
            <div className="field-hint">Sin programas disponibles para este proceso.</div>
          )}
        </div>
      </div>

      {processInfo && (
        <div className="process-info">
          <div className="info-title">📋 Información del Proceso</div>
          <div className="info-cost-row">
            <span>Programa</span>
            <strong>{processInfo.denomination}</strong>
          </div>
          {processInfo.loading && (
            <div className="info-cost-row"><span style={{ color: 'var(--gray-500)' }}>Cargando costos...</span></div>
          )}
          {!processInfo.loading && processInfo.costs && processInfo.costs.length > 0 && (
            <>
              <div style={{ marginTop: '10px', fontSize: '0.78rem', fontWeight: 700, color: 'var(--green-dark)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>
                💰 Costos del proceso
              </div>
              {processInfo.costs.map((c, i) => (
                <div key={i} className="info-cost-row">
                  <span>{c.Concept?.denomination || 'Concepto'}</span>
                  <strong>
                    {c.cant > 1 ? `${c.cant} × ` : ''}S/ {parseFloat(c.amount || 0).toFixed(2)}
                  </strong>
                </div>
              ))}
              <div className="info-cost-row" style={{ borderTop: '1px solid rgba(0,121,60,0.2)', paddingTop: '6px', marginTop: '4px' }}>
                <span style={{ fontWeight: 700 }}>Total</span>
                <strong style={{ fontSize: '1rem' }}>
                  S/ {processInfo.costs.reduce((s, c) => s + parseFloat(c.amount || 0) * (c.cant || 1), 0).toFixed(2)}
                </strong>
              </div>
            </>
          )}

          {!processInfo.loading && processInfo.inscriptionCosts && processInfo.inscriptionCosts.length > 1 && (
            <div style={{ marginTop: '12px' }}>
              <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--green-dark)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>
                🎫 Tipo de Inscripción <span style={{ color: '#e53e3e' }}>*</span>
              </div>
              <select
                className="form-select"
                value={state.selectedCostId || ''}
                onChange={(e) => onUpdate({ selectedCostId: e.target.value ? Number(e.target.value) : null })}
                style={{ marginTop: '4px' }}
              >
                <option value="">Seleccione el tipo de inscripción...</option>
                {processInfo.inscriptionCosts.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.Concept?.denomination || 'Inscripción'} — S/ {parseFloat(c.amount || 0).toFixed(2)}
                  </option>
                ))}
              </select>
            </div>
          )}
          {!processInfo.loading && processInfo.inscriptionCosts && processInfo.inscriptionCosts.length === 1 && (
            <div style={{ marginTop: '10px', fontSize: '0.8rem', color: '#2d7a4f', background: 'rgba(0,121,60,0.07)', borderRadius: '6px', padding: '6px 10px', display: 'flex', justifyContent: 'space-between' }}>
              <span>🎫 {processInfo.inscriptionCosts[0].Concept?.denomination || 'Inscripción'}</span>
              <strong>S/ {parseFloat(processInfo.inscriptionCosts[0].amount || 0).toFixed(2)}</strong>
            </div>
          )}
        </div>
      )}

      <div className="step-actions">
        <div />
        <button className="btn-primary" onClick={onNext} disabled={!canContinue}>
          Continuar →
        </button>
      </div>
    </div>
  );
}
